export interface DampedHoltOptions {
  levelSmoothing?: number
  trendSmoothing?: number
  damping?: number
  maximumRelativeResidual?: number
}

function median(values: readonly number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((left, right) => left - right)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle]
}

function boundedUnit(value: number, fallback: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback
}

/**
 * Robust damped Holt smoothing for display. The residual bound lets the curve
 * react to a weak or strong month without allowing one observation to redefine
 * the whole pace. Forecast calculations remain separate from this visual line.
 */
export function dampedHoltTrend(
  values: readonly number[],
  projectedPoints = 0,
  options: DampedHoltOptions = {},
): number[] {
  const safeValues = values.map(value => Number.isFinite(value) ? Math.max(0, value) : 0)
  if (safeValues.length === 0) return []
  if (safeValues.length === 1) return Array.from({ length: 1 + Math.max(0, projectedPoints) }, () => safeValues[0])

  const levelSmoothing = boundedUnit(options.levelSmoothing ?? 0.45, 0.45)
  const trendSmoothing = boundedUnit(options.trendSmoothing ?? 0.2, 0.2)
  const damping = boundedUnit(options.damping ?? 0.85, 0.85)
  const maximumRelativeResidual = Math.max(0, options.maximumRelativeResidual ?? 0.5)
  const slopes: number[] = []
  for (let from = 0; from < safeValues.length - 1; from += 1) {
    for (let to = from + 1; to < safeValues.length; to += 1) {
      slopes.push((safeValues[to] - safeValues[from]) / (to - from))
    }
  }

  let level = safeValues[0]
  let trend = median(slopes)
  const result = [level]
  for (let index = 1; index < safeValues.length; index += 1) {
    const predicted = Math.max(0, level + damping * trend)
    const residualLimit = Math.max(1, predicted * maximumRelativeResidual)
    const residual = Math.min(residualLimit, Math.max(-residualLimit, safeValues[index] - predicted))
    const boundedObservation = predicted + residual
    const previousLevel = level
    level = Math.max(0, levelSmoothing * boundedObservation + (1 - levelSmoothing) * predicted)
    trend = trendSmoothing * (level - previousLevel) + (1 - trendSmoothing) * damping * trend
    result.push(level)
  }

  for (let index = 0; index < Math.max(0, projectedPoints); index += 1) {
    trend *= damping
    level = Math.max(0, level + trend)
    result.push(level)
  }
  return result
}
